import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import ShippingFeesSearchSection from "../../../../../components/search-sections/shipping-fees";
import {routeFunctions, routes} from "../../../../../../core/models/static/routes/app-routes";
import Api from "../../../../../../core/services/api/api";
import {useDataGridSavedState} from "../../../../../../packages/data-grid";
import ShippingFeesListViewTable from "./sections/table";
import {ShippingFeesDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import Button from "../../../../../components/base/button/button";
import {useAppSelector} from "../../../../../redux";
import {Link} from "react-router-dom";
import {Typography} from "@mui/material";
import useQuery from "../../../../../hooks/use-query";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useOperatorInformation} from "../../../../../hooks/use-operator-information";

const tabs = {
    confirmed: 'confirmed',
    notConfirmed: 'not-confirmed',
    allRecords: 'all-records',
}

const tabsList = [
    {title: 'Not Confirmed', value: tabs.notConfirmed},
    {title: 'Confirmed', value: tabs.confirmed},
    {title: 'All Records', value: tabs.allRecords},
];

const defaultTab = tabs.notConfirmed;

const exportingChunkSize = 100;

const ShippingFeesListView = () => {
    const [query] = useQuery();
    const [data, setData] = useState([]);
    const [updatedTab, setUpdatedTab] = useState(query?.tab ?? defaultTab);
    const isMounted = useIsMounted();
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const {configureNotebookTags} = useConfigureNotebook('shipping-fees-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportShippingFeesList = operatorAccessLevels.packages.sales.exportShippingFeesList;
    const operatorInformation = useOperatorInformation();

    /**
     * Fetches the customer admin information from the server.
     * @type {function(*, *): string}
     */
    const getInvoiceLink = useCallback((customerId, invoiceId) => routeFunctions.customer.sales.invoices.single(customerId, invoiceId), [])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        if (defaultCustomer?.code) {
            configureNotebookTags((tags) => tags.customerShippingFeesList(defaultCustomer?.code));
            configureBehavioralTool((tags) => tags.customerShippingFeesList(defaultCustomer?.code));
        } else {
            configureNotebookTags((tags) => tags.shippingFeesList);
            configureBehavioralTool((tags) => tags.shippingFeesList);
        }
    }, [configureBehavioralTool, configureNotebookTags, defaultCustomer?.code]);

    const [notConfirmedDataGridSaveConfig, confirmedDataGridSaveConfig, allRecordsDataGridSaveConfig] = useMemo(() => [
        ({
            name: 'ShippingFeesListViewNotConfirmedDataGrid',
            version: 2,
        }),
        ({
            name: 'ShippingFeesListViewConfirmedDataGrid',
            version: 2,
        }),
        ({
            name: 'ShippingFeesListViewAllRecordsDataGrid',
            version: 2,
        })
    ], [])

    const [, notConfirmedDataGridSortBy, notConfirmedDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: notConfirmedDataGridSaveConfig.name,
        version: notConfirmedDataGridSaveConfig.version,
    });
    const [, confirmedDataGridSortBy, confirmedDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: confirmedDataGridSaveConfig.name,
        version: confirmedDataGridSaveConfig.version,
    });
    const [, allRecordsDataGridSortBy, allRecordsDataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: allRecordsDataGridSaveConfig.name,
        version: allRecordsDataGridSaveConfig.version,
    });
    const [dataGridSortBy, dataGridPageSize] = useMemo(() => {
        switch (updatedTab) {
            case tabs.notConfirmed:
                return [notConfirmedDataGridSortBy, notConfirmedDataGridPageSize]
            case tabs.confirmed:
                return [confirmedDataGridSortBy, confirmedDataGridPageSize]
            case tabs.allRecords:
                return [allRecordsDataGridSortBy, allRecordsDataGridPageSize]
            default:
                return [];
        }
    }, [allRecordsDataGridPageSize, allRecordsDataGridSortBy, confirmedDataGridPageSize, confirmedDataGridSortBy, notConfirmedDataGridPageSize, notConfirmedDataGridSortBy, updatedTab])

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        tab: updatedTab,
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
        customers: [],
    }), [updatedTab, initialPaginationInfo, dataGridSortBy]);

    /**
     * Prepares the search data that is to be used to search for the shipping fees of the system.
     * @type {(function(UseSearchFunctionArg): any)}
     */
    const prepareSearchData = useCallback((args) => {
        const customerIds = !!defaultCustomer?.id ? [defaultCustomer?.id] : args.customers?.map(e => e.id) ?? [];
        const forApi = {
            keyword: args.filters?.keyword?.toString(),
            startDate: args.filters?.startDate,
            endDate: args.filters?.endDate,
            loadConfirmedOnly: args.tab === tabs.confirmed ? true : args.tab === tabs.notConfirmed ? false : null,
            customerIds: !!customerIds?.length ? customerIds : null,
            pagination: args.paginationInfo,
        }
        if (args.orderBy?.field) {
            forApi['sorts'] = [{
                property: args.orderBy.field,
                isDesc: args.orderBy.descending,
            }];
        }
        return forApi;
    }, [defaultCustomer?.id])

    /**
     * Searches through the shipping fees of the system with the available searching filters.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = prepareSearchData(args);
        const response = await Api.listFreightOrders(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const list = response?.data?.items?.map(e => ({
                ...e,
                confirmingActualCost: false,
                actualCostConfirmed: !!e?.reviewedBy,
                actualCostInEditMode: false,
                actualCostConfirmedLocally: false,

                confirmingActualDate: false,
                actualDateConfirmed: !!e?.actualDelivery,
                actualDateInEditMode: false,
                actualDateConfirmedLocally: false,

            })) ?? []
            if (!forExport) {
                setData(list);
                args.setPaginationInfo(prevState => ({
                    ...prevState,
                    length: response?.data?.totalCount ?? 0
                }));
            } else {
                response.data.items = list;
                return response;
            }
        }
    }, [isMounted, prepareSearchData])

    /**
     * Searches among the orders based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const searchEntries = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const searchData = useSearch(searchEntries, initialSearchDataSettings)

    /**
     * Creates a csv formatted file of all the entries of the current shipping fees tab.
     * @returns {Promise<DataGridExportInfo | undefined>}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.items?.length) {
                break;
            }
            entries.push(...res.data.items);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        if (!isMounted() || !entries.length)
            return;
        let exportTitle = `Shipping fees`
        const toBeExported = entries?.map(o => {
            return {
                "Invoice No": o?.invoiceNo,
                "Customer": o?.customerName,
                "Freight Company": o?.freightCompanyTitle,
                "Is Free": o?.isFree ? 'Yes' : 'No',
                "Reason": o?.friendlyReason,
                "Freight Quote": o?.estimatedFee,
                "Customer Charges": o?.customerFee,
                "Actual Fee": o?.actualFee,
                "Estimated Delivery Date": o?.estimatedDeliveryDateTime ? DateUtils.serverToLocal(o?.estimatedDeliveryDateTime).format(DateUtils.dateTimeFormat) : '',
                "Actual Delivery Date": o?.actualDelivery ? DateUtils.serverToLocal(o?.actualDelivery).format(DateUtils.dateTimeFormat) : '',
                "Submitted By": o?.submittedBy ?? 'N/A',
                "Submitted Date": o?.submittedDateTime ? DateUtils.serverToLocal(o?.submittedDateTime).format(DateUtils.dateTimeFormat) : 'N/A',
                "Confirmed": tabs.confirmed === searchData.tab ? 'Yes' : tabs.notConfirmed === searchData.tab ? 'No' : !!o?.reviewedBy ? 'Yes' : 'No',
                "Confirmed By": o?.reviewedBy ?? 'N/A',
            }
        }) ?? [];
        switch (searchData.tab) {
            case tabs.confirmed:
                exportTitle = 'Confirmed '.concat(exportTitle)
                break
            case tabs.notConfirmed:
                exportTitle = 'Not Confirmed '.concat(exportTitle)
                break
            case tabs.allRecords:
                exportTitle = 'All '.concat(exportTitle)
                break
            default:
                break;
        }
        return {
            title: exportTitle,
            body: toBeExported,
        }
    }, [fetchEntries, isMounted, searchData])

    /**
     * With each change in the [tab], updates the value of the [updatedTab] state.
     */
    useLayoutEffect(() => setUpdatedTab(searchData.tab), [searchData.tab])

    /**
     * Changes the tab of the view to the given value.
     */
    const onTabChanged = useCallback((e, newTab) => {
        setData([]);
        searchData.setTab(newTab);
        switch (newTab) {
            case tabs.notConfirmed:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: notConfirmedDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(notConfirmedDataGridSortBy);
                break;
            case tabs.confirmed:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: confirmedDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(confirmedDataGridSortBy);
                break;
            case tabs.allRecords:
                searchData.setPaginationInfo((prevState) => ({
                    ...prevState,
                    pageSize: allRecordsDataGridPageSize ?? initialPaginationInfo.pageSize,
                }));
                searchData.setOrderBy(allRecordsDataGridSortBy);
                break;
            default:
                return;
        }
    }, [allRecordsDataGridPageSize, allRecordsDataGridSortBy, confirmedDataGridPageSize, confirmedDataGridSortBy, initialPaginationInfo.pageSize, notConfirmedDataGridPageSize, notConfirmedDataGridSortBy, searchData]);

    /**
     * Merges the properties of a shipping fee that is associated with the given id with the given props.
     * @param {number} id
     * @param {any} props
     * @type {(function(number, any): void)}
     */
    const setShippingFeeProps = useCallback((id, props = {}) => {
        setData(prevState => prevState?.map(e => e.id === id
            ? {...e, ...(props ?? {})}
            : e
        ))
    }, [])

    /**
     * Updates the actual fee of the given shipping fee in the server.
     *
     * @param {any} shippingFee
     * @param {string} actualCost
     * @returns {Promise<void>}
     */
    const updateShippingFeesActualFee = useCallback(async (shippingFee, actualCost) => {
        setShippingFeeProps(shippingFee.id, {confirmingActualCost: true})
        const forApi = {
            id: shippingFee?.id,
            actualFee: Number(actualCost),
            updatedProperties: ['ActualFee'],
        }
        const response = await Api.updateFreightOrder(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            const submittedBy = operatorInformation?.name ?? "";
            setShippingFeeProps(shippingFee.id,
                {
                    actualCostConfirmed: true,
                    actualCostConfirmedLocally: true,
                    actualFee: forApi.actualFee,
                    actualCostInEditMode: false,
                    reviewedBy: submittedBy,
                })
        }
        setShippingFeeProps(shippingFee.id, {confirmingActualCost: false})
    }, [isMounted, operatorInformation?.name, setShippingFeeProps])

    /**
     * Confirms the actual delivery date of the given shipping fee in the server.
     *
     * @param {any} shippingFee
     * @param {DateUtilsTypes.Instance | null} actualDate
     * @returns {Promise<void>}
     */
    const updateShippingFeesActualDeliveryDate = useCallback(async (shippingFee, actualDate) => {
        setShippingFeeProps(shippingFee.id, {confirmingActualDate: true})
        const forApi = {
            id: shippingFee?.id,
            actualDelivery: actualDate,
            updatedProperties: ['ActualDelivery'],
        }
        const response = await Api.updateFreightOrder(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setShippingFeeProps(shippingFee.id,
                {
                    actualDateConfirmed: true,
                    actualDateConfirmedLocally: true,
                    actualDelivery: forApi.actualDelivery,
                    actualDateInEditMode: false,
                })
        }
        setShippingFeeProps(shippingFee.id, {confirmingActualDate: false})
    }, [isMounted, setShippingFeeProps])

    return (
        <>
            <div className={'shipping-fees-list-view'}>
                <div className={'d-flex justify-content-between align-content-center mb-4'}>
                    <Typography variant={'h6'} fontWeight={600}>
                        {defaultCustomer?.name ? `${defaultCustomer?.name}'s` : "System"} Shipping Fees
                    </Typography>
                    <div className={'d-flex align-content-center justify-content-end'}>
                        {
                            !!defaultCustomer?.id &&
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                component={Link}
                                to={routes.shippingFees.list}
                            >
                                View all shipping fees
                            </Button>
                        }
                    </div>
                </div>
                <ShippingFeesSearchSection
                    searchData={searchData}
                    hideCustomersMultiSelect={!!defaultCustomer?.id}
                />
                <div className={'mt-4 w-100'}/>
                <div className={'flex-grow-1 position-relative'}>
                    <Tabs value={searchData.tab ?? false} onChange={onTabChanged}>
                        {
                            tabsList?.map(e => (
                                <Tab key={e.value} label={e.title} value={e.value}/>
                            ))
                        }
                    </Tabs>
                </div>
                <div className='my-4 w-100'/>

                {
                    searchData.tab === tabs.notConfirmed &&
                    <ShippingFeesListViewTable
                        dataGridSaveConfig={notConfirmedDataGridSaveConfig}
                        searchData={searchData}
                        data={data}
                        exportToCsv={canExportShippingFeesList ? exportToCsv : undefined}
                        updateActualFee={updateShippingFeesActualFee}
                        updateActualDate={updateShippingFeesActualDeliveryDate}
                        setShippingFeeProps={setShippingFeeProps}
                        feeEditableAfterUpdate={false}
                        excludeColumns={[ShippingFeesDataGridColumnNames.confirmedBy]}
                        getInvoiceLink={getInvoiceLink}
                    />
                }
                {
                    searchData.tab === tabs.confirmed &&
                    <ShippingFeesListViewTable
                        dataGridSaveConfig={notConfirmedDataGridSaveConfig}
                        data={data}
                        searchData={searchData}
                        exportToCsv={canExportShippingFeesList ? exportToCsv : undefined}
                        updateActualFee={updateShippingFeesActualFee}
                        updateActualDate={updateShippingFeesActualDeliveryDate}
                        setShippingFeeProps={setShippingFeeProps}
                        getInvoiceLink={getInvoiceLink}
                    />
                }
                {
                    searchData.tab === tabs.allRecords &&
                    <ShippingFeesListViewTable
                        dataGridSaveConfig={notConfirmedDataGridSaveConfig}
                        data={data}
                        searchData={searchData}
                        exportToCsv={canExportShippingFeesList ? exportToCsv : undefined}
                        updateActualFee={updateShippingFeesActualFee}
                        updateActualDate={updateShippingFeesActualDeliveryDate}
                        setShippingFeeProps={setShippingFeeProps}
                        getInvoiceLink={getInvoiceLink}
                    />
                }
            </div>
        </>
    )
}


export default ShippingFeesListView;
